import { useEffect } from "react"
import type { CalculatorGroupsState } from "./useCalculatorGroupsState"
import { useFollowedUsers } from "src/contexts"
import { useGlucoseUnit } from "src/hooks/account"
import { InsightCalculatorGroup } from "src/types"
import type { CalculationWorkerParamsData } from "src/workers/insightCalculationWorker"

export function useInsightCalculationWorker(
  calculatorGroupsState: CalculatorGroupsState,
  queryEndTime: string,
  timeSinceWakeUpInHours: number,
  worker: Worker,
): void {
  const glucoseUnit = useGlucoseUnit()
  const { currentFollow } = useFollowedUsers()

  const {
    range_normal_top: normalRangeTop,
    range_normal_bottom: normalRangeBottom,
  } = currentFollow.display_settings

  const {
    start_time: sleepStart,
    end_time: sleepEnd,
  } = currentFollow.sleep_settings

  const accountId = currentFollow.following_account_id

  const exerciseInsightRanges = calculatorGroupsState[InsightCalculatorGroup.Exercise]

  useEffect(
    () => {
      if (!worker) {
        return
      }

      const insightRanges = exerciseInsightRanges

      const message: CalculationWorkerParamsData = {
        accountId,
        calculationType: InsightCalculatorGroup.Exercise,
        insightRanges,
        queryEndTime,
        timeSinceWakeUpInHours,
        params: {
          insightRanges,
          timeSinceWakeUpInHours,
        },
      }

      worker.postMessage(message)
    },
    [
      accountId,
      worker,
      exerciseInsightRanges,
      queryEndTime,
      timeSinceWakeUpInHours,
    ],
  )

  const foodInsightRanges = calculatorGroupsState[InsightCalculatorGroup.Food]

  useEffect(
    () => {
      if (!worker) {
        return
      }

      const insightRanges = foodInsightRanges

      const message: CalculationWorkerParamsData = {
        accountId,
        calculationType: InsightCalculatorGroup.Food,
        params: {
          insightRanges,
          timeSinceWakeUpInHours,
        },
        insightRanges,
        queryEndTime,
        timeSinceWakeUpInHours,
      }

      worker.postMessage(message)
    },
    [
      accountId,
      worker,
      foodInsightRanges,
      queryEndTime,
      timeSinceWakeUpInHours,
    ],
  )

  const glucoseInsightRanges = calculatorGroupsState[InsightCalculatorGroup.Glucose]

  useEffect(
    () => {
      if (!worker) {
        return
      }

      const insightRanges = glucoseInsightRanges

      const message: CalculationWorkerParamsData = {
        accountId,
        calculationType: InsightCalculatorGroup.Glucose,
        params: {
          glucoseUnit,
          insightRanges,
          normalRangeTop,
          normalRangeBottom,
          sleepEnd,
          sleepStart,
          timeSinceWakeUpInHours,
        },
        insightRanges,
        queryEndTime,
        timeSinceWakeUpInHours,
      }

      worker.postMessage(message)
    },
    [
      accountId,
      worker,
      glucoseUnit,
      glucoseInsightRanges,
      normalRangeTop,
      normalRangeBottom,
      sleepEnd,
      sleepStart,
      queryEndTime,
      timeSinceWakeUpInHours,
    ],
  )

  const timeInRangeInsightState = calculatorGroupsState[InsightCalculatorGroup.TimeInRange]
  useEffect(
    () => {
      if (!worker) {
        return
      }

      const message: CalculationWorkerParamsData = {
        accountId,
        calculationType: InsightCalculatorGroup.TimeInRange,
        insightRanges: timeInRangeInsightState ? [0] : [], // TiR doesn't need insight ranges.
        queryEndTime,
        timeSinceWakeUpInHours: 0,
        params: {
          normalRangeTop,
          normalRangeBottom,
          queryEndTime,
        },
      }

      worker.postMessage(message)
    },
    [
      accountId,
      worker,
      normalRangeTop,
      normalRangeBottom,
      queryEndTime,
      timeInRangeInsightState,
    ],
  )

  const insulinInsightRanges = calculatorGroupsState[InsightCalculatorGroup.Insulin]

  useEffect(
    () => {
      if (!worker) {
        return
      }

      const insightRanges = insulinInsightRanges

      const message: CalculationWorkerParamsData = {
        accountId,
        calculationType: InsightCalculatorGroup.Insulin,
        params: {
          insightRanges,
          timeSinceWakeUpInHours,
        },
        insightRanges,
        queryEndTime,
        timeSinceWakeUpInHours,
      }

      worker.postMessage(message)
    },
    [
      accountId,
      worker,
      insulinInsightRanges,
      queryEndTime,
      timeSinceWakeUpInHours,
    ],
  )

  const liquidInsightRanges = calculatorGroupsState[InsightCalculatorGroup.Liquid]

  useEffect(
    () => {
      if (!worker) {
        return
      }

      const insightRanges = liquidInsightRanges

      const message: CalculationWorkerParamsData = {
        accountId,
        calculationType: InsightCalculatorGroup.Liquid,
        params: {
          insightRanges,
          timeSinceWakeUpInHours,
        },
        insightRanges,
        queryEndTime,
        timeSinceWakeUpInHours,
      }

      worker.postMessage(message)
    },
    [
      accountId,
      worker,
      liquidInsightRanges,
      queryEndTime,
      timeSinceWakeUpInHours,
    ],
  )

  const quartileInsightRanges = calculatorGroupsState[InsightCalculatorGroup.Quartile]

  useEffect(
    () => {
      if (!worker) {
        return
      }

      const insightRanges = quartileInsightRanges

      const message: CalculationWorkerParamsData = {
        accountId,
        calculationType: InsightCalculatorGroup.Quartile,
        insightRanges,
        queryEndTime,
        timeSinceWakeUpInHours,
        params: undefined,
      }

      worker.postMessage(message)
    },
    [
      accountId,
      worker,
      quartileInsightRanges,
      queryEndTime,
      timeSinceWakeUpInHours,
    ],
  )
}
